import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { ManageKitsLink } from '../ui/ManageLink';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { CellInput, SheetAddRow, SheetSection } from '../ui/Sheet';
import { newKey, type DraftLine } from './types';

type Found = { type: 'item' | 'kit'; id: string; name: string; categoryId: string | null };
type Option = PickerOption & { found: Found };

const NO_CATEGORY = 'Bez kategorii';
const KITS = 'Zestawy';
const KITS_KEY = '#kits';
const NONE_KEY = '';

/** Lines by section key: a catalog category's id (known categories), `NONE_KEY` (none or unknown), `KITS_KEY` (kits). */
function sectionKey(l: DraftLine, known: ReadonlySet<string>): string {
  if (l.kitId) return KITS_KEY;
  return l.categoryId && known.has(l.categoryId) ? l.categoryId : NONE_KEY;
}

/** Column count after the section column: Sprzęt, Ilość and (editing) the remove button. */
export const sheetSpan = (canEdit: boolean) => 2 + (canEdit ? 1 : 0);

/**
 * The content of a block or template as a sheet, like the quote's and the packlist's (owner remark 4/32): a section
 * per catalog category in the catalog's order, the quantity edited in its cell, each section ending with its own quick
 * picker (that category's gear suggested first), "+ Sekcja" with the catalog's categories and one picker for anything
 * (also kits) under the table, and the count of lines and pieces. `leading` goes into the table first (the
 * template's blocks). The same item or kit picked again adds to its line; a new line's quantity takes the focus, Enter
 * there goes back to the picker.
 */
export function LinesSheet({
  lines,
  onChange,
  canEdit,
  label,
  featureId,
  leading,
  summary,
}: {
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  canEdit: boolean;
  /** The bottom picker's label (e.g. "Dodaj do szablonu"). */
  label: string;
  featureId: string;
  leading?: ReactNode;
  /** More to say in the summary under the table (e.g. "bloków: 2"). */
  summary?: string;
}) {
  const { me } = useMe();
  const categories = useCategories(!!me && can(me, 'catalog.categories', 'view'));
  const order = categoryLabels(categories.data?.categories ?? []);
  const labelOf = new Map(order.map((c) => [c.id, c.label]));
  const known = new Set(labelOf.keys());
  /** Sections opened with "+ Sekcja" that have no lines yet (kept only while the editor is open). */
  const [opened, setOpened] = useState<string[]>([]);
  const [focusSection, setFocusSection] = useState<string | null>(null);
  const [focusLine, setFocusLine] = useState<number | null>(null);
  const pickers = useRef(new Map<string, HTMLInputElement>());
  const quantities = useRef(new Map<number, HTMLInputElement>());
  /** The picker that added the line whose quantity has the focus: Enter goes back there. */
  const returnTo = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (focusSection === null) return;
    pickers.current.get(focusSection)?.focus();
    setFocusSection(null);
  }, [focusSection]);
  useEffect(() => {
    if (focusLine === null) return;
    const input = quantities.current.get(focusLine);
    input?.focus();
    input?.select();
    setFocusLine(null);
  }, [focusLine]);

  const groups = new Map<string, DraftLine[]>();
  for (const l of lines) {
    const key = sectionKey(l, known);
    groups.set(key, [...(groups.get(key) ?? []), l]);
  }
  for (const id of opened) if (known.has(id) && !groups.has(id)) groups.set(id, []);
  const rank = new Map(order.map((c, i) => [c.id, i]));
  const place = (key: string) => (key === KITS_KEY ? Infinity : key === NONE_KEY ? order.length : (rank.get(key) ?? 0));
  const sections = [...groups.entries()].sort(([a], [b]) => place(a) - place(b));
  const free = order.filter((c) => !groups.has(c.id));
  const span = sheetSpan(canEdit);
  const pieces = lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);

  async function search(q: string, signal: AbortSignal, categoryId?: string): Promise<Option[]> {
    const params = new URLSearchParams({ q });
    if (categoryId) params.set('categoryId', categoryId);
    // a role may see the catalog but not kits (or the other way round): what is forbidden is simply not offered
    const [items, kits] = await Promise.all([
      api<{ items: { id: string; name: string; categoryId: string | null }[] }>(`/api/catalog/items/suggest?${params.toString()}`, { signal }).catch(() => ({ items: [] })),
      categoryId ? Promise.resolve({ kits: [] }) : api<{ kits: { id: string; name: string }[] }>(`/api/kits?q=${encodeURIComponent(q)}`, { signal }).catch(() => ({ kits: [] })),
    ]);
    const found: Found[] = [
      ...items.items.map((i) => ({ type: 'item' as const, id: i.id, name: i.name, categoryId: i.categoryId })),
      ...kits.kits.slice(0, 5).map((k) => ({ type: 'kit' as const, id: k.id, name: k.name, categoryId: null })),
    ];
    return found.map((f) => ({ key: `${f.type}-${f.id}`, name: f.name, details: [f.type === 'kit' ? 'zestaw' : ((f.categoryId && labelOf.get(f.categoryId)) ?? NO_CATEGORY.toLowerCase())], found: f }));
  }

  function pick(option: Option, quantity: number) {
    const { type, id, name, categoryId } = option.found;
    const active = document.activeElement;
    returnTo.current = active instanceof HTMLInputElement ? active : null;
    const same = lines.find((l) => (type === 'item' ? l.itemId === id : l.kitId === id));
    if (same) {
      onChange(lines.map((l) => (l === same ? { ...l, quantity: String((Number(l.quantity) || 0) + quantity) } : l)));
      setFocusLine(same.key);
      return;
    }
    const line: DraftLine = { key: newKey(), itemId: type === 'item' ? id : null, kitId: type === 'kit' ? id : null, name, categoryId, categoryName: (categoryId && labelOf.get(categoryId)) || null, quantity: String(quantity) };
    onChange([...lines, line]);
    setFocusLine(line.key);
  }

  function quantityKeys(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    // Enter in a quantity: back to the picker it came from (the form is saved with its button)
    e.preventDefault();
    returnTo.current?.focus();
  }
  const pickerRef = (key: string) => (el: HTMLInputElement | null) => {
    if (el) pickers.current.set(key, el);
    else pickers.current.delete(key);
  };
  const quantityRef = (key: number) => (el: HTMLInputElement | null) => {
    if (el) quantities.current.set(key, el);
    else quantities.current.delete(key);
  };

  return (
    <div className="tpl-sheet" data-feature-id={featureId}>
      <div className="table-wrap">
        <table className="table sheet-table tpl-table" aria-label="Pozycje">
          <thead>
            <tr>
              <th scope="col" className="sheet-section-col">
                Kategoria
              </th>
              <th scope="col">Sprzęt</th>
              <th scope="col" className="num">
                Ilość
              </th>
              {canEdit && (
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              )}
            </tr>
          </thead>
          {leading}
          {sections.map(([key, group]) => {
            const name = key === KITS_KEY ? KITS : key === NONE_KEY ? NO_CATEGORY : labelOf.get(key)!;
            return (
              <SheetSection key={key || 'none'} name={name}>
                {group.map((l, i) => (
                  <tr key={l.key} className="sheet-row">
                    <th scope="row" className="sheet-section-cell">
                      {i === 0 && <span className="sheet-section-name">{name}</span>}
                    </th>
                    <td className="sheet-main">{l.name}</td>
                    <td className="num tpl-qty">
                      {canEdit ? (
                        <CellInput
                          ref={quantityRef(l.key)}
                          numeric
                          inputMode="numeric"
                          aria-label={`Ilość: ${l.name}`}
                          aria-invalid={/^\d+$/.test(l.quantity.trim()) && Number(l.quantity) > 0 ? undefined : true}
                          value={l.quantity}
                          onChange={(e) => onChange(lines.map((x) => (x.key === l.key ? { ...x, quantity: e.target.value } : x)))}
                          onKeyDown={quantityKeys}
                        />
                      ) : (
                        l.quantity
                      )}
                    </td>
                    {canEdit && (
                      <td className="q-actions">
                        <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń: ${l.name}`} title="Usuń" onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {group.length === 0 && (
                  <tr className="sheet-empty">
                    <th scope="row" className="sheet-section-cell">
                      <span className="sheet-section-name">{name}</span>
                    </th>
                    <td colSpan={span} className="muted">
                      Brak pozycji.{' '}
                      <button className="btn small ghost" type="button" onClick={() => setOpened(opened.filter((id) => id !== key))}>
                        Usuń sekcję
                      </button>
                    </td>
                  </tr>
                )}
                {canEdit && key !== NONE_KEY && key !== KITS_KEY && (
                  <SheetAddRow span={span}>
                    <QuickPicker<Option>
                      label={`Dodaj do sekcji: ${name}`}
                      labelHidden
                      placeholder="Dodaj pozycję, np. 4 sm58"
                      search={(q, signal) => search(q, signal, key)}
                      onPick={pick}
                      inputRef={pickerRef(key)}
                      featureId={featureId}
                    />
                  </SheetAddRow>
                )}
              </SheetSection>
            );
          })}
          {lines.length === 0 && sections.length === 0 && (
            <tbody>
              <tr className="sheet-empty">
                <th scope="row" className="sheet-section-cell" />
                <td colSpan={span} className="muted">
                  Nie ma jeszcze pozycji. Dodaj sprzęt lub zestaw poniżej albo zacznij od sekcji.
                </td>
              </tr>
            </tbody>
          )}
          {canEdit && (
            <tbody className="tpl-foot">
              <tr className="sheet-add">
                <th scope="row" className="sheet-section-cell">
                  {free.length > 0 && (
                    <select
                      className="pl-add-category"
                      aria-label="Dodaj sekcję"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        setOpened([...opened, id]);
                        setFocusSection(id);
                      }}
                    >
                      <option value="">+ Sekcja</option>
                      {free.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  )}
                </th>
                <td colSpan={span}>
                  <QuickPicker<Option> label={label} labelHidden placeholder="Dodaj sprzęt lub zestaw, np. qrp 40, 4x kabel xlr" search={(q, signal) => search(q, signal)} onPick={pick} inputRef={pickerRef(NONE_KEY)} featureId={featureId} />
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      <p className="tpl-summary muted" aria-label="Podsumowanie">
        Pozycji: {lines.length} · sztuk razem: {pieces}
        {summary ? ` · ${summary}` : ''}
      </p>
      {canEdit && me && <ManageKitsLink me={me} />}
    </div>
  );
}
